import * as Api from '@ViewModels';
import { Moment } from 'moment';
import * as React from 'react';
import { Topics } from '../../../../models/LocalNotificationTopics';
import { useLocalNotificationService } from '../../../../models/LocalNotifications';
import {
	applyFacebookMentions,
	convertHtmlStringValueToPlainText,
	createContentStateWithHtmlStringValue,
	getShortenedString,
	mergeFacebookMentions,
	parseFacebookMentions,
} from '../../../../models/UiUtils';
import {
	IMediaReplacements,
	IPlatformCustomizationDraft,
	applyMediaChange,
	createCustomizationFromBase,
	findPlatformCustomization,
	fromApiPlatformCustomizations,
	getPublishedContent,
	getTargetedPlatforms,
	getUnmatchedMedia,
	inferSplitMedia,
	isSameMedia,
	materializePlatformCustomizations,
	reconcileBaseMedia,
	recordUploads,
	trackReplacements,
	toApiPlatformCustomizations,
} from '../../../../models/utils/platformCustomizations';
import { useUserSession } from '../../../../models/hooks/appStateHooks';
import type { CropSource } from '../../../components-v2/UnifiedImageDialog/types';
import type { ICropFraming } from '../../../components-v2/ImageCropDialog';

export interface IEditSocialMediaPostContext {
	aiReference?: Api.IAIReference;
	/** Internally set to true any time content or images change */
	hasChanges?: boolean;
	postContent?: string;
	/**
	 * The base post's media, which the composer edits while the post is one post. Once split no tab shows
	 * it: it is worked out from the split's media after every change (see reconcileBaseMedia), because the
	 * API still checks it against the post-wide limits, and it is what turning the split off keeps.
	 */
	postImages?: Api.IFileAttachment[];
	/** Pages picked from the mention dropdown; each "@Page Name" in postContent that names one is stored as its token. */
	facebookMentions?: Api.IFacebookPageLookupResult[];
	postTargets?: Api.IPostTarget[];
	campaignName?: string;
	isReviewMode?: boolean;
	schedulePost?(
		selectedMoment?: Moment,
		suggestionId?: string,
		impersonationContext?: Api.IImpersonationContext,
		bypassCompliance?: boolean,
		sendWithComplianceEmail?: string,
		forceSendWithCompliance?: boolean
	): Promise<readonly [Api.SocialMediaPostViewModel, Api.IOperationResultNoValue]>;
	selectedCreator?: Api.IUser;
	setAIReference?(aiReference: Api.IAIReference | ((currentValue: Api.IAIReference) => Api.IAIReference)): void;
	setContent?(postContent: string | ((currentValue: string) => string)): void;
	setCampaignName?(campaignName: string | ((currentValue: string) => string)): void;
	setHasChanges?(hasChanges: boolean | ((currentValue: boolean) => boolean)): void;
	setIsReviewMode?(isReviewMode: boolean | ((currentValue: boolean) => boolean)): void;
	/**
	 * Loads the base post's media. Split, the media the versions start from is read back from it and them,
	 * as `loadPlatformCustomizations` does, so either may load first. Edits go through `setActiveImages`.
	 */
	setImages?(
		postImages?: Api.IFileAttachment[] | ((currentValue: Api.IFileAttachment[]) => Api.IFileAttachment[])
	): void;
	/**
	 * Adds media to what the composer shows: `platform`'s version while split, which the caller names
	 * because an upload can outlive the tab it began on, or the one post otherwise. The additions count as
	 * uploads, which the base holds for the API. `provider` gets that media and the base, and returns the
	 * media with the additions in place.
	 */
	addActiveImages?(provider: ActiveImagesProvider, platform: Api.SocialMediaType | undefined): void;
	/** Crops, edits, removes, or reorders what the composer shows, as `addActiveImages` names it. */
	setActiveImages?(provider: ActiveImagesProvider, platform: Api.SocialMediaType | undefined): void;
	setFacebookMentions?(
		facebookMentions?:
			| Api.IFacebookPageLookupResult[]
			| ((currentValue: Api.IFacebookPageLookupResult[]) => Api.IFacebookPageLookupResult[])
	): void;
	/** Loads stored content: its mention tokens become "@Page Name" text and the mentioned Pages. */
	loadContent?(content?: string): void;
	setPostTargets?(postTargets?: Api.IPostTarget[] | ((currentValue: Api.IPostTarget[]) => Api.IPostTarget[])): void;
	setSelectedCreator?(selectedCreator: Api.IUser | ((currentValue: Api.IUser) => Api.IUser)): void;
	setTemplate?(template?: Api.ITemplate | ((currentValue: Api.ITemplate) => Api.ITemplate)): void;
	template?: Api.ITemplate;
	updatePost?(
		// targets: Api.IPostTarget[],
		postOrId: string | Api.SocialMediaPostViewModel,
		selectedMoment?: Moment,
		bypassCompliance?: boolean,
		sendWithComplianceEmail?: string,
		forceSendWithCompliance?: boolean
	): Promise<readonly [Api.IOperationResult<Api.ISocialMediaPost>, Api.IOperationResultNoValue]>;
	getPostBase?: () => Api.ISocialMediaPostBase;
	overlayLogo?: boolean;
	setOverlayLogo?(overlayLogo: boolean | ((currentValue: boolean) => boolean)): void;
	logoSize?: number;
	setLogoSize?(logoSize: number | ((currentValue: number) => number)): void;
	hideWhiteBackground?: boolean;
	setHideWhiteBackground?(hideWhiteBackground: boolean | ((currentValue: boolean) => boolean)): void;
	/** While true the base post publishes nowhere — every targeted platform publishes its own version. */
	customizeByPlatform?: boolean;
	platformCustomizations?: IPlatformCustomizationDraft[];
	/**
	 * The base media when the split began: what each version starts from, and what Reset returns one to.
	 * The base itself goes on collecting every tab's uploads, which a version seeded from it would inherit.
	 */
	splitBaseImages?: Api.IFileAttachment[];
	/** Media uploaded on a tab since the split began, which the base holds while a targeted tab does. */
	splitUploads?: Api.IFileAttachment[];
	/** Which original each crop or edit made since the split began stands in for. */
	splitReplacements?: IMediaReplacements;
	/**
	 * What each crop made in this session was cut from, by the crop's id: the first original however often
	 * it was cropped since, and the framing last used. It lets a crop be framed again or undone. The API
	 * keeps no record of it, so a crop saved in an earlier session can do neither.
	 */
	cropSources?: Readonly<Record<string, CropSource>>;
	/** Records that `cropped` was cut from `original` with `framing`; see `cropSources`. */
	recordCrop?(original: Api.IFileAttachment, cropped: Api.IFileAttachment, framing: ICropFraming): void;
	/** Turning this on copies the base into a version per targeted platform; turning it off discards them. */
	setCustomizeByPlatform?(customizeByPlatform: boolean): void;
	setPlatformCustomization?(
		platform: Api.SocialMediaType,
		customization:
			| Partial<IPlatformCustomizationDraft>
			| ((currentValue: IPlatformCustomizationDraft) => Partial<IPlatformCustomizationDraft>)
	): void;
	resetPlatformCustomization?(platform: Api.SocialMediaType): void;
	/**
	 * Seeds the split from a stored post's or template's versions, and adds the Pages they mention to the
	 * picked set, without marking the draft changed. Run after `loadContent`, which replaces that set.
	 */
	loadPlatformCustomizations?(platformCustomizations: readonly Api.IPostPlatformCustomization[] | undefined): void;
	/** The platform version on screen. Shared so the composer and the preview stay on the same one. */
	activePlatform?: Api.SocialMediaType;
	setActivePlatform?(platform: Api.SocialMediaType): void;
}

/** Returns the media with the change made, or `currentValue` itself when there is nothing to change. */
type ActiveImagesProvider = (
	currentValue: readonly Api.IFileAttachment[],
	baseMedia: readonly Api.IFileAttachment[]
) => readonly Api.IFileAttachment[];

export const EditSocialMediaPostContext = React.createContext<IEditSocialMediaPostContext>(null);

export const useEditSocialMediaPostContext = () => {
	const context = React.useContext(EditSocialMediaPostContext);
	if (!context) {
		throw new Error(`This component requires the use of "EditSocialMediaPostContext"`);
	}
	return context;
};

export const EditSocialMediaPostContextProvider: React.FC<
	React.PropsWithChildren<{
		contextRef?: React.MutableRefObject<IEditSocialMediaPostContext>;
	}>
> = ({ children, contextRef }) => {
	const [context] = useCreateEditSocialMediaContext();
	if (contextRef) {
		contextRef.current = context;
	}

	return <EditSocialMediaPostContext.Provider value={context}>{children}</EditSocialMediaPostContext.Provider>;
};

/** A stored split post's base read back into what the composer keeps; see inferSplitMedia. */
const readSplitMedia = (
	base: readonly Api.IFileAttachment[],
	drafts: readonly IPlatformCustomizationDraft[]
): Pick<IEditSocialMediaPostContext, 'splitBaseImages' | 'splitReplacements' | 'splitUploads'> => {
	if (!drafts.length) {
		return { splitBaseImages: undefined, splitReplacements: undefined, splitUploads: undefined };
	}
	const { replacements, seed, uploads } = inferSplitMedia(base, drafts);
	return { splitBaseImages: seed, splitReplacements: replacements, splitUploads: uploads };
};

/** The media a version starts from: the base as it was when the split began. */
const getVersionSeedImages = (currentContext: IEditSocialMediaPostContext) =>
	currentContext.splitBaseImages ?? currentContext.postImages ?? [];

/** The draft's versions with one platform's changed, starting it from the base when it has none yet. */
const updatePlatformCustomization = (
	currentContext: IEditSocialMediaPostContext,
	platform: Api.SocialMediaType,
	update: (current: IPlatformCustomizationDraft) => Partial<IPlatformCustomizationDraft>
): IPlatformCustomizationDraft[] => {
	const existing = currentContext.platformCustomizations ?? [];
	const current =
		existing.find(customization => customization.platform === platform) ??
		createCustomizationFromBase(platform, currentContext.postContent, getVersionSeedImages(currentContext));
	const updated = { ...current, ...update(current), platform };
	return existing.some(customization => customization.platform === platform)
		? existing.map(customization => (customization.platform === platform ? updated : customization))
		: [...existing, updated];
};

/**
 * Every change to the context ends here, so the base's rules hold whichever path made it; see
 * reconcileBaseMedia. Nothing is targeted when a post is not split, or is customized but targets nothing,
 * and then the base is the one post the composer shows.
 */
const withBaseReconciled = (currentContext: IEditSocialMediaPostContext): IEditSocialMediaPostContext => {
	const targetedPlatforms = getTargetedPlatforms(currentContext.postTargets);
	if (!currentContext.customizeByPlatform || !targetedPlatforms.length) {
		return currentContext;
	}
	const base = currentContext.postImages ?? [];
	const next = reconcileBaseMedia(
		getVersionSeedImages(currentContext),
		currentContext.splitUploads ?? [],
		currentContext.platformCustomizations ?? [],
		targetedPlatforms,
		currentContext.splitReplacements
	);
	// The same array when nothing moved: a template post reads a new one as edited media.
	return isSameMedia(base, next) ? currentContext : { ...currentContext, postImages: next };
};

/**
 * One media edit from the composer, landing on `platform`: the version the panel showed when the edit
 * began, which a crop still uploading can outlive. An addition there is recorded as an upload, which
 * the base holds for the API. Customized with nothing targeted, the composer shows one post, so the
 * edit reaches the base, the media versions start from, and every stored version alike.
 */
const editActiveImages = (
	currentContext: IEditSocialMediaPostContext,
	provider: ActiveImagesProvider,
	platform: Api.SocialMediaType | undefined,
	isUpload: boolean
): IEditSocialMediaPostContext => {
	const base = currentContext.postImages ?? [];
	if (!currentContext.customizeByPlatform || !platform) {
		const next = provider(base, base);
		if (next === base) {
			return currentContext;
		}
		return currentContext.customizeByPlatform
			? {
					...currentContext,
					hasChanges: true,
					platformCustomizations: (currentContext.platformCustomizations ?? []).map(customization => ({
						...customization,
						attachments: applyMediaChange(customization.attachments, base, next),
					})),
					postImages: [...next],
					splitBaseImages: applyMediaChange(getVersionSeedImages(currentContext), base, next),
				}
			: { ...currentContext, hasChanges: true, postImages: [...next] };
	}
	const held =
		findPlatformCustomization(currentContext.platformCustomizations, platform)?.attachments ??
		getVersionSeedImages(currentContext);
	const attachments = provider(held, base);
	if (attachments === held) {
		return currentContext;
	}
	return {
		...currentContext,
		hasChanges: true,
		platformCustomizations: updatePlatformCustomization(currentContext, platform, () => ({
			attachments: [...attachments],
		})),
		splitReplacements: isUpload
			? currentContext.splitReplacements
			: trackReplacements(currentContext.splitReplacements ?? {}, held, attachments),
		splitUploads: isUpload
			? recordUploads(
					currentContext.splitUploads ?? [],
					getVersionSeedImages(currentContext),
					attachments,
					getUnmatchedMedia(attachments, held),
					currentContext.splitReplacements
				)
			: currentContext.splitUploads,
	};
};

export const useCreateEditSocialMediaContext = () => {
	const userSession = useUserSession();
	const setHasChangesRef = React.useRef<(hasChanges?: boolean) => void>(null);
	const setContentRef = React.useRef<(content?: string) => void>(null);
	const setImagesRef = React.useRef<(images?: Api.IFileAttachment[]) => void>(null);
	const addActiveImagesRef =
		React.useRef<(provider: ActiveImagesProvider, platform: Api.SocialMediaType | undefined) => void>(null);
	const setActiveImagesRef =
		React.useRef<(provider: ActiveImagesProvider, platform: Api.SocialMediaType | undefined) => void>(null);
	const setFacebookMentionsRef = React.useRef<(facebookMentions?: Api.IFacebookPageLookupResult[]) => void>(null);
	const loadContentRef = React.useRef<(content?: string) => void>(null);
	const setPostTargetsRef = React.useRef<(targets?: Api.IPostTarget[]) => void>(null);
	const setSelectedCreatorRef = React.useRef<(selectedCreator?: Api.IUser) => void>(null);
	const setTemplateRef = React.useRef<(template?: Api.ITemplate) => void>(null);
	const { postNotification } = useLocalNotificationService<Api.SocialMediaPostViewModel>();
	const setAIReferenceRef = React.useRef<(aiReference?: Api.IAIReference) => void>(null);
	const setCampaignNameRef = React.useRef<(campaignName?: string) => void>(null);
	const setIsReviewModeRef = React.useRef<(isReviewMode?: boolean) => void>(null);
	const setOverlayLogoRef = React.useRef<(overlayLogo?: boolean) => void>(null);
	const setLogoSizeRef = React.useRef<(logoSize?: number) => void>(null);
	const setHideWhiteBackgroundRef = React.useRef<(hideWhiteBackground?: boolean) => void>(null);
	const setCustomizeByPlatformRef = React.useRef<(customizeByPlatform: boolean) => void>(null);
	const setPlatformCustomizationRef =
		React.useRef<(platform: Api.SocialMediaType, next: Partial<IPlatformCustomizationDraft>) => void>(null);
	const resetPlatformCustomizationRef = React.useRef<(platform: Api.SocialMediaType) => void>(null);
	const loadPlatformCustomizationsRef =
		React.useRef<(customizations: readonly Api.IPostPlatformCustomization[] | undefined) => void>(null);
	const setActivePlatformRef = React.useRef<(platform: Api.SocialMediaType) => void>(null);
	const recordCropRef =
		React.useRef<(original: Api.IFileAttachment, cropped: Api.IFileAttachment, framing: ICropFraming) => void>(null);

	const schedulePostRef =
		React.useRef<
			(
				selectedMoment?: Moment,
				suggestionId?: string,
				impersonationContext?: Api.IImpersonationContext,
				bypassCompliance?: boolean,
				sendWithComplianceEmail?: string,
				forceSendWithCompliance?: boolean
			) => Promise<readonly [Api.SocialMediaPostViewModel, Api.IOperationResultNoValue]>
		>(null);
	const updatePostRef =
		React.useRef<
			(
				postOrId: string | Api.SocialMediaPostViewModel,
				selectedMoment?: Moment,
				bypassCompliance?: boolean,
				sendWithComplianceEmail?: string,
				forceSendWithCompliance?: boolean
			) => Promise<readonly [Api.IOperationResult<Api.ISocialMediaPost>, Api.IOperationResultNoValue]>
		>(null);
	const getPostBase = React.useRef(() => {
		const { aiReference, campaignName, template, postContent, postImages, facebookMentions } = contextRef.current;
		const unwrappedContent = convertHtmlStringValueToPlainText(template?.content.document);
		const isCustomized = unwrappedContent !== postContent || template?.attachments !== postImages;
		const wrappedContent = createContentStateWithHtmlStringValue(applyFacebookMentions(postContent, facebookMentions));
		// A post split before it was written has an empty base, so its name comes from the first version with words.
		const namedContent = postContent?.trim()
			? postContent
			: (getPublishedContent(contextRef.current).find(content => content.trim()) ?? '');
		const post: Api.ISocialMediaPostBase = {
			aiReference,
			attachments: postImages,
			content: {
				document: wrappedContent.getRawRichTextContent().document,
			},
			name:
				campaignName ||
				(template?.id ? template.name : namedContent ? getShortenedString(namedContent, 25, ' ') : 'Custom Post'),
			templateReference: template?.id
				? {
						isCustomized,
						isSystemTemplate: template.scope
							? !![Api.TemplateScope.Industry, Api.TemplateScope.System].find(x => x === template.scope)
							: false,
						name: template.name,
						templateId: template.id,
					}
				: null,
			designatedTargets: contextRef.current?.postTargets,
			overlayLogo: contextRef.current.overlayLogo,
			logoSize: contextRef.current.overlayLogo ? contextRef.current.logoSize : undefined,
			// Carrying versions is what marks a post customized, so split off sends an empty set rather
			// than a flag, and a post never keeps variants it no longer publishes.
			platformCustomizations: contextRef.current.customizeByPlatform
				? toApiPlatformCustomizations(
						contextRef.current.platformCustomizations,
						getTargetedPlatforms(contextRef.current.postTargets),
						facebookMentions
					)
				: [],
		};
		return post;
	}).current;

	const contextRef = React.useRef<IEditSocialMediaPostContext>(null);
	const [context, setStoredContext] = React.useState<IEditSocialMediaPostContext>(() => {
		const ctx: Partial<IEditSocialMediaPostContext> = {
			campaignName: '',
			hasChanges: false,
			postContent: '',
			postImages: [],
			facebookMentions: [],
			postTargets: [],
			selectedCreator: null,
			template: null,
			overlayLogo: false,
			logoSize: Api.DEFAULT_LOGO_SIZE,
			hideWhiteBackground: false,
			isReviewMode: false,
			customizeByPlatform: false,
			platformCustomizations: [],
		};
		ctx.setAIReference = aiReferenceProvider => {
			setAIReferenceRef.current?.(
				typeof aiReferenceProvider === 'function'
					? aiReferenceProvider(contextRef.current.aiReference)
					: aiReferenceProvider
			);
		};

		ctx.setHasChanges = booleanProvider => {
			setHasChangesRef.current?.(
				typeof booleanProvider === 'function' ? booleanProvider(contextRef.current.hasChanges) : booleanProvider
			);
		};

		ctx.setImages = imageProvider => {
			setImagesRef.current?.(
				typeof imageProvider === 'function' ? imageProvider(contextRef.current.postImages) : imageProvider
			);
		};

		ctx.addActiveImages = (provider, platform) => addActiveImagesRef.current?.(provider, platform);
		ctx.setActiveImages = (provider, platform) => setActiveImagesRef.current?.(provider, platform);

		ctx.setFacebookMentions = facebookMentionsProvider => {
			setFacebookMentionsRef.current?.(
				typeof facebookMentionsProvider === 'function'
					? facebookMentionsProvider(contextRef.current.facebookMentions)
					: facebookMentionsProvider
			);
		};

		ctx.setContent = contentProvider => {
			setContentRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.postContent) : contentProvider
			);
		};

		ctx.loadContent = content => {
			loadContentRef.current?.(content);
		};

		ctx.setSelectedCreator = creatorProvider => {
			setSelectedCreatorRef.current?.(
				typeof creatorProvider === 'function' ? creatorProvider(contextRef.current.selectedCreator) : creatorProvider
			);
		};

		ctx.setPostTargets = targetsProvider => {
			setPostTargetsRef.current?.(
				typeof targetsProvider === 'function' ? targetsProvider(contextRef.current.postTargets) : targetsProvider
			);
		};

		ctx.setTemplate = contentProvider => {
			setTemplateRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.template) : contentProvider
			);
		};

		ctx.setCampaignName = contentProvider => {
			setCampaignNameRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.campaignName) : contentProvider
			);
		};

		ctx.setIsReviewMode = contentProvider => {
			setIsReviewModeRef.current?.(
				typeof contentProvider === 'function' ? contentProvider(contextRef.current.isReviewMode) : contentProvider
			);
		};

		ctx.setOverlayLogo = overlayLogoProvider => {
			setOverlayLogoRef.current?.(
				typeof overlayLogoProvider === 'function'
					? overlayLogoProvider(contextRef.current.overlayLogo)
					: overlayLogoProvider
			);
		};
		ctx.setLogoSize = logoSizeProvider => {
			setLogoSizeRef.current?.(
				typeof logoSizeProvider === 'function' ? logoSizeProvider(contextRef.current.logoSize) : logoSizeProvider
			);
		};
		ctx.setHideWhiteBackground = hideWhiteBackgroundProvider => {
			setHideWhiteBackgroundRef.current?.(
				typeof hideWhiteBackgroundProvider === 'function'
					? hideWhiteBackgroundProvider(contextRef.current.hideWhiteBackground)
					: hideWhiteBackgroundProvider
			);
		};

		ctx.schedulePost = (
			selectedMoment,
			suggestionId,
			impersonationContext,
			bypassCompliance,
			sendWithComplianceEmail,
			forceSendWithCompliance
		) =>
			schedulePostRef.current?.(
				selectedMoment,
				suggestionId,
				impersonationContext,
				bypassCompliance,
				sendWithComplianceEmail,
				forceSendWithCompliance
			);

		ctx.setCustomizeByPlatform = customizeByPlatform => setCustomizeByPlatformRef.current?.(customizeByPlatform);

		ctx.setPlatformCustomization = (platform, customizationProvider) => {
			const current =
				contextRef.current.platformCustomizations?.find(customization => customization.platform === platform) ??
				createCustomizationFromBase(platform, contextRef.current.postContent, getVersionSeedImages(contextRef.current));
			setPlatformCustomizationRef.current?.(
				platform,
				typeof customizationProvider === 'function' ? customizationProvider(current) : customizationProvider
			);
		};

		ctx.resetPlatformCustomization = platform => resetPlatformCustomizationRef.current?.(platform);

		ctx.loadPlatformCustomizations = platformCustomizations =>
			loadPlatformCustomizationsRef.current?.(platformCustomizations);

		ctx.setActivePlatform = platform => setActivePlatformRef.current?.(platform);

		ctx.recordCrop = (original, cropped, framing) => recordCropRef.current?.(original, cropped, framing);

		ctx.getPostBase = getPostBase;
		ctx.updatePost = (postOrId, selectedMoment, bypassCompliance, sendWithComplianceEmail, forceSendWithCompliance) =>
			updatePostRef.current?.(
				postOrId,
				selectedMoment,
				bypassCompliance,
				sendWithComplianceEmail,
				forceSendWithCompliance
			);

		contextRef.current = ctx;
		return ctx;
	});
	React.useEffect(() => {
		contextRef.current = context;
	});
	const setContext = React.useRef(
		(update: (currentContext: IEditSocialMediaPostContext) => IEditSocialMediaPostContext) =>
			setStoredContext(currentContext => withBaseReconciled(update(currentContext)))
	).current;

	if (!setHasChangesRef.current) {
		setHasChangesRef.current = (value?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: value,
			}));
		};
	}

	if (!setAIReferenceRef.current) {
		setAIReferenceRef.current = (aiReference?: Api.IAIReference) => {
			setContext(currentContext => ({
				...currentContext,
				aiReference,
				hasChanges: true,
			}));
		};
	}

	if (!setContentRef.current) {
		setContentRef.current = (content?: string) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				postContent: content || '',
			}));
		};
	}
	if (!setImagesRef.current) {
		setImagesRef.current = (images?: Api.IFileAttachment[]) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				postImages: images || [],
				...(currentContext.customizeByPlatform
					? readSplitMedia(images || [], currentContext.platformCustomizations ?? [])
					: {}),
			}));
		};
	}
	if (!addActiveImagesRef.current) {
		addActiveImagesRef.current = (provider, platform) =>
			setContext(currentContext => editActiveImages(currentContext, provider, platform, true));
	}
	if (!setActiveImagesRef.current) {
		setActiveImagesRef.current = (provider, platform) =>
			setContext(currentContext => editActiveImages(currentContext, provider, platform, false));
	}
	if (!setFacebookMentionsRef.current) {
		setFacebookMentionsRef.current = (mentions?: Api.IFacebookPageLookupResult[]) => {
			setContext(currentContext => ({
				...currentContext,
				facebookMentions: mentions || [],
				hasChanges: true,
			}));
		};
	}
	if (!loadContentRef.current) {
		loadContentRef.current = (content?: string) => {
			const parsed = parseFacebookMentions(content);
			setContext(currentContext => ({
				...currentContext,
				facebookMentions: parsed.mentions,
				hasChanges: true,
				postContent: parsed.content,
			}));
		};
	}
	if (!setSelectedCreatorRef.current) {
		setSelectedCreatorRef.current = (creator?: Api.IUser) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: false,
				selectedCreator: creator || null,
			}));
		};
	}
	if (!setTemplateRef.current) {
		setTemplateRef.current = (template?: Api.ITemplate) =>
			setContext(currentContext => ({
				...currentContext,
				template,
			}));
	}

	if (!setPostTargetsRef.current) {
		setPostTargetsRef.current = (targets?: Api.IPostTarget[]) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: false,
				postTargets: targets || [],
				// Targeting a platform while split is on has to give it an entry here: the server rejects a
				// missing one rather than publishing the base in its place.
				platformCustomizations: currentContext.customizeByPlatform
					? materializePlatformCustomizations(
							getTargetedPlatforms(targets),
							currentContext.platformCustomizations,
							currentContext.postContent,
							getVersionSeedImages(currentContext)
						)
					: currentContext.platformCustomizations,
			}));
		};
	}

	if (!setCampaignNameRef.current) {
		setCampaignNameRef.current = (name?: string) => {
			setContext(currentContext => ({
				...currentContext,
				campaignName: name,
				hasChanges: false,
			}));
		};
	}

	if (!setIsReviewModeRef.current) {
		setIsReviewModeRef.current = (isReviewMode?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				isReviewMode,
			}));
		};
	}

	if (!setOverlayLogoRef.current) {
		setOverlayLogoRef.current = (overlayLogo?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				overlayLogo,
			}));
		};
	}
	if (!setLogoSizeRef.current) {
		setLogoSizeRef.current = (logoSize?: number) => {
			setContext(currentContext => ({
				...currentContext,
				logoSize,
				hasChanges: true,
			}));
		};
	}
	if (!setHideWhiteBackgroundRef.current) {
		setHideWhiteBackgroundRef.current = (hideWhiteBackground?: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				hideWhiteBackground,
			}));
		};
	}

	if (!setCustomizeByPlatformRef.current) {
		setCustomizeByPlatformRef.current = (customizeByPlatform: boolean) => {
			setContext(currentContext => ({
				...currentContext,
				customizeByPlatform,
				hasChanges: true,
				splitBaseImages: customizeByPlatform ? [...(currentContext.postImages ?? [])] : undefined,
				splitReplacements: customizeByPlatform ? {} : undefined,
				splitUploads: customizeByPlatform ? [] : undefined,
				// Turning it off is destructive by contract: the versions go, and there is no retained copy.
				// The base keeps every tab's uploads; crops and edits held only by a version go with it.
				platformCustomizations: customizeByPlatform
					? materializePlatformCustomizations(
							getTargetedPlatforms(currentContext.postTargets),
							currentContext.platformCustomizations,
							currentContext.postContent,
							currentContext.postImages
						)
					: [],
			}));
		};
	}

	if (!setPlatformCustomizationRef.current) {
		setPlatformCustomizationRef.current = (
			platform: Api.SocialMediaType,
			next: Partial<IPlatformCustomizationDraft>
		) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				platformCustomizations: updatePlatformCustomization(currentContext, platform, () => next),
			}));
		};
	}

	if (!resetPlatformCustomizationRef.current) {
		resetPlatformCustomizationRef.current = (platform: Api.SocialMediaType) => {
			setContext(currentContext => ({
				...currentContext,
				hasChanges: true,
				platformCustomizations: (currentContext.platformCustomizations ?? []).map(customization =>
					customization.platform === platform
						? createCustomizationFromBase(platform, currentContext.postContent, getVersionSeedImages(currentContext))
						: customization
				),
			}));
		};
	}

	if (!loadPlatformCustomizationsRef.current) {
		loadPlatformCustomizationsRef.current = (
			platformCustomizations: readonly Api.IPostPlatformCustomization[] | undefined
		) => {
			const { drafts, mentions } = fromApiPlatformCustomizations(platformCustomizations);
			setContext(currentContext => ({
				...currentContext,
				// Stored versions are what mark a post customized; an unsplit post stores none.
				customizeByPlatform: drafts.length > 0,
				facebookMentions: mergeFacebookMentions(currentContext.facebookMentions, mentions),
				platformCustomizations: drafts,
				...readSplitMedia(currentContext.postImages ?? [], drafts),
			}));
		};
	}

	if (!recordCropRef.current) {
		recordCropRef.current = (original, cropped, framing) => {
			setContext(currentContext => ({
				...currentContext,
				cropSources: {
					...currentContext.cropSources,
					[cropped.id]: { framing, original: currentContext.cropSources?.[original.id]?.original ?? original },
				},
			}));
		};
	}

	if (!setActivePlatformRef.current) {
		setActivePlatformRef.current = (platform: Api.SocialMediaType) => {
			setContext(currentContext => ({
				...currentContext,
				activePlatform: platform,
			}));
		};
	}

	if (!schedulePostRef.current) {
		schedulePostRef.current = async (
			selectedMoment?: Moment,
			suggestionId?: string,
			impersonationContext?: Api.IImpersonationContext,
			bypasssCompliance?: boolean,
			sendWithComplianceEmail?: string,
			forceSendWithCompliance?: boolean
		) => {
			const basePost = getPostBase();
			const post: Api.ICreateSocialMediaPostRequest = {
				...basePost,
				dueDate: selectedMoment?.toISOString(),
				forUserId: contextRef.current?.selectedCreator?.id,
				// RegEd forces compliance on so the server builds the approval request the filing keys off,
				// independent of the legacy complianceSettings.enabled flag.
				sendWithCompliance:
					forceSendWithCompliance ||
					impersonationContext?.account?.preferences?.complianceSettings?.enabled ||
					userSession?.account?.preferences?.complianceSettings?.enabled
						? !bypasssCompliance
						: undefined,
				targets: undefined,
				sendWithComplianceEmail,
			};

			let error: Api.IOperationResultNoValue = null;
			let result: Api.SocialMediaPostViewModel = null;
			try {
				result = await Api.SocialMediaPostViewModel.schedulePost(userSession, post, suggestionId, impersonationContext);
				postNotification({
					info: result,
					topic: Topics.CREATE_SOCIAL_POST,
				});
			} catch (err) {
				error = err;
			}
			return [result, error] as const;
		};
	}

	if (!updatePostRef.current) {
		updatePostRef.current = async (
			postOrId: string | Api.SocialMediaPostViewModel,
			selectedMoment?: Moment,
			bypassCompliance?: boolean,
			sendWithComplianceEmail?: string,
			forceSendWithCompliance?: boolean
		) => {
			const viewModel =
				typeof postOrId === 'string' ? new Api.SocialMediaPostViewModel(userSession, { id: postOrId }) : postOrId;
			const basePost = getPostBase();
			// If sendWithComplianceEmail is provided, enable compliance (for Draft → Scheduled with compliance).
			// Otherwise preserve existing compliance state (can only bypass, not enable). RegEd forces it
			// on so the server builds the approval request the filing keys off.
			const sendWithCompliance = forceSendWithCompliance
				? !bypassCompliance
				: sendWithComplianceEmail
					? !bypassCompliance
					: viewModel.sendWithCompliance
						? !bypassCompliance
						: null;
			const updatedPost: Api.ISocialMediaPost = {
				...basePost,
				creator: viewModel.creator || contextRef.current.selectedCreator,
				dueDate: selectedMoment?.toISOString(),
				id: viewModel.id,
				name: contextRef.current.campaignName || viewModel.name || basePost.name,
				sendWithCompliance,
				sendWithComplianceEmail,
				status: viewModel.status,
				targets: [],
			};

			let error: Api.IOperationResultNoValue = null;
			let result: Api.IOperationResult<Api.ISocialMediaPost> = null;
			try {
				result = await viewModel.update(updatedPost);
				postNotification({
					info: viewModel,
					topic: Topics.EDIT_SOCIAL_POST,
				});
			} catch (err) {
				error = err;
			}
			return [result, error] as const;
		};
	}

	return [context, setContext] as const;
};
